<template>
  <div class="pa-3">
    <v-snackbar top right v-model="snackbar" :color="snackbar_color" :timeout="4000">
      <span>{{ snackbar_message }}</span>
    </v-snackbar>

    <v-row v-if="loading">
      <v-col class="text-center">
        <v-progress-linear color="info" height="10" indeterminate striped></v-progress-linear>
      </v-col>
    </v-row>

    <template v-else>
      <!-- ── Header card ─────────────────────────────────────────────── -->
      <v-card class="neu-glow mb-3">
        <v-card-title class="py-2">
          <v-icon class="mr-2 info--text">mdi-credit-card</v-icon>
          Pagamenti Stripe
          <v-tooltip top >
            <template v-slot:activator="{ on, attrs }">
              <v-btn v-bind="attrs" v-on="on" icon fab small class="ml-4" :loading="loading" @click="reloadAll()">
                <v-icon color="info">mdi-sync-circle</v-icon>
              </v-btn>
            </template>
            <span>Aggiorna</span>
          </v-tooltip>
          <v-spacer></v-spacer>
          <div>
            <v-card-text class="py-0">
              <v-chip class="mr-2 success "><v-icon small left>mdi-check-circle</v-icon>{{ totalSucceeded }}</v-chip>
              <v-chip class="mr-2 warning "><v-icon small left>mdi-clock-alert</v-icon>{{ totalRequiresCapture }}</v-chip>
              <v-chip class="mr-2 grey"><v-icon small left>mdi-cancel</v-icon>{{ totalCanceled }}</v-chip>
            </v-card-text>            
          </div>
          <v-spacer></v-spacer>
          <v-row dense class="d-flex justify-end">
            <div  class="ml-3">
              <v-select v-model="filterStatus" :items="statusOptions" label="Stato" outlined dense clearable hide-details></v-select>
            </div>
            <div  class="ml-3">
              <v-text-field outlined v-model="search" append-icon="mdi-magnify" label="Cerca:" dense color="info" single-line hide-details style="max-width:250px"></v-text-field>
            </div>
            <div class="ml-3">
              <v-btn style="min-height:40px" @click="dialog_range=true">Periodo {{ rangeString }}</v-btn>
            </div>
          </v-row>

        </v-card-title>

      </v-card>

      <!-- ── Tabs + action buttons on the same row ─────────────────── -->
      <div class="d-flex align-center mb-3">
        <v-tabs v-model="activeTab" background-color="transparent" color="info" style="flex:1; min-width:0">
          <v-tab><v-icon left small>mdi-credit-card-multiple</v-icon>Pagamenti</v-tab>
          <v-tab @click="loadTransfers()"><v-icon left small>mdi-bank-transfer</v-icon>Storni / Bonifici</v-tab>
        </v-tabs>
        <div v-if="selectedPayments.length" class="d-flex align-center flex-shrink-0 pl-3">
          <v-btn small outlined color="grey" class="mr-2" @click="selectedPayments=[]">Deseleziona</v-btn>
          <v-btn small color="info" :disabled="transferNet <= 0" @click="dialog_transfer=true">
            <v-icon left small>mdi-bank-transfer</v-icon>
            Trasferisci al ristorante
          </v-btn>
        </div>
      </div>

      <v-tabs-items v-model="activeTab">

        <!-- ══ TAB 1 — Payments list ══════════════════════════════════ -->
        <v-tab-item>

          <v-card class="neu-glow">
            <v-data-table
              v-model="selectedPayments"
              :headers="headers"
              :items="filteredPayments"
              :items-per-page="50"
              :footer-props="{ itemsPerPageOptions: [10,20,50] }"
              show-select
              item-key="payment_intent_id"
              sort-by="created_at"
              sort-desc
              class="elevation-0"
              :no-data-text="'Nessun pagamento trovato'"
            >
              <!-- ── Select-all: only selectable rows (succeeded + not transferred) -->
              <template v-slot:[`header.data-table-select`]>
                <v-simple-checkbox
                  :value="selectablePayments.length > 0 && selectablePayments.every(p => selectedPayments.some(s => s.payment_intent_id === p.payment_intent_id))"
                  :indeterminate="selectedPayments.length > 0 && !selectablePayments.every(p => selectedPayments.some(s => s.payment_intent_id === p.payment_intent_id))"
                  @input="toggleSelectAll"
                  color="info"
                ></v-simple-checkbox>
              </template>

              <!-- ── Tooltip on every header ── -->
              <template v-for="h in headers.filter(c => c.tooltip)" v-slot:[`header.${h.value}`]="{ header }">
                <v-tooltip bottom :key="h.value" max-width="260">
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on" style="cursor:default">
                      {{ header.text }}
                      <v-icon x-small color="grey lighten-1" class="ml-1">mdi-help-circle-outline</v-icon>
                    </span>
                  </template>
                  <span>{{ h.tooltip }}</span>
                </v-tooltip>
              </template>

              <!-- Override checkbox to only allow selection of succeeded+not-transferred rows -->
              <template v-slot:[`item.data-table-select`]="{ item, isSelected, select }">
                <v-simple-checkbox
                  v-if="item.status === 'succeeded' && !item.transfer_id"
                  :value="isSelected"
                  @input="select($event)"
                  color="info"
                ></v-simple-checkbox>
                <v-icon v-else-if="item.transfer_id" small color="success">mdi-check-decagram</v-icon>
                <span v-else></span>
              </template>

              <template v-slot:[`item.amount`]="{ item }">
                <span class="font-weight-bold">{{ Number(item.amount).toFixed(2) }}</span>
              </template>
              <template v-slot:[`item.application_fee_amount`]="{ item }">
                <span v-if="item.application_fee_amount != null" class="orange--text">- {{ Number(item.application_fee_amount).toFixed(2) }}</span>
                <span v-else class="grey--text text--lighten-2">—</span>
              </template>
              <template v-slot:[`item.stripe_fees`]="{ item }">
                <span v-if="item.stripe_fees != null" class="orange--text">- {{ Number(item.stripe_fees).toFixed(2) }}</span>
                <span v-else class="grey--text text--lighten-2">—</span>
              </template>
              <template v-slot:[`item.net_amount`]="{ item }">
                <span v-if="item.amount != null" class="font-weight-bold teal--text">
                  {{ (Number(item.amount) - Number(item.application_fee_amount || 0) - Number(item.stripe_fees || 0)).toFixed(2) }}
                </span>
                <span v-else class="grey--text text--lighten-2">—</span>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip small :color="statusColor(item.status)">
                  <v-icon small left>{{ statusIcon(item.status) }}</v-icon><span class="grey--text text--lighten-2"></span>
                  {{ statusLabel(item.status) }}
                </v-chip>
              </template>
              <template v-slot:[`item.transfer_id`]="{ item }">
                <v-chip v-if="item.transfer_id" small color="teal" dark>
                  <v-icon small left>mdi-check-decagram</v-icon>Trasferito
                </v-chip>
              </template>

              <template v-slot:[`item.eligible`]="{ item }">
                <v-icon :color="item.eligible ? 'success' : 'grey lighten-1'">
                  {{ item.eligible ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                </v-icon>
              </template>

              <template v-slot:[`item.not_arrived`]="{ item }">
                <v-icon :color="item.not_arrived ? 'warning' : 'grey lighten-1'">
                  {{ item.not_arrived ? 'mdi-ghost' : '' }}
                </v-icon>
              </template>              

              <template v-slot:[`item.exceed`]="{ item }">
                <template v-if="item.status === 'requires_capture'">
                  <v-icon :color="item.exceed ? 'warning' : 'grey lighten-1'">
                    {{ item.exceed ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline' }}
                  </v-icon>
                </template>
                <span v-else class="grey--text text--lighten-2">—</span>
              </template>
              <template v-slot:[`item.booking_date`]="{ item }">
                <span class="caption">{{ formatDateTime(item.booking_date) }}</span>
              </template>
              <template v-slot:[`item.created_at`]="{ item }">
                <span class="caption">{{ formatDateTime(item.created_at) }}</span>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <v-tooltip top v-if="item.status === 'requires_capture'">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon small color="warning" :loading="capturing === item.payment_intent_id" @click="confirmAction('capture', item)">
                      <v-icon small>mdi-cash-check</v-icon>
                    </v-btn>
                  </template>
                  <span>Cattura pagamento (no-show)</span>
                </v-tooltip>
                <v-tooltip top v-if="item.status === 'requires_capture'">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon small color="error" :loading="canceling === item.payment_intent_id" @click="confirmAction('cancel', item)">
                      <v-icon small>mdi-cancel</v-icon>
                    </v-btn>
                  </template>
                  <span>Annulla autorizzazione</span>
                </v-tooltip>
                <v-tooltip top v-if="item.status === 'succeeded'">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon small color="error" :loading="refunding === item.payment_intent_id" @click="confirmAction('refund', item)">
                      <v-icon small>mdi-cash-refund</v-icon>
                    </v-btn>
                  </template>
                  <span>Rimborsa pagamento</span>
                </v-tooltip>
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon small color="blue-grey" :href="'https://dashboard.stripe.com/payments/' + item.payment_intent_id" target="_blank">
                      <v-icon small>mdi-open-in-new</v-icon>
                    </v-btn>
                  </template>
                  <span>Apri in Stripe</span>
                </v-tooltip>
                <v-tooltip top v-if="item.receipt_url">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" icon small color="teal" :href="item.receipt_url" target="_blank">
                      <v-icon small>mdi-receipt</v-icon>
                    </v-btn>
                  </template>
                  <span>Ricevuta di pagamento</span>
                </v-tooltip>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>

        <!-- ══ TAB 2 — Transfers history ════════════════════════════ -->
        <v-tab-item>
          <v-card class="neu-glow mb-2" flat v-if="transferSummary">
            <v-card-text class="py-2 d-flex flex-wrap">
              <v-chip small class="mr-2 teal white--text"><v-icon small left>mdi-sigma</v-icon>Totale trasferito (netto):  {{ Number(transferSummary.total_net || 0).toFixed(2) }}</v-chip>
              <v-chip small class="mr-2 info white--text"><v-icon small left>mdi-bank-transfer</v-icon>N° bonifici: {{ transferSummary.total_count || 0 }}</v-chip>
              <v-chip small class="mr-2 orange white--text"><v-icon small left>mdi-percent</v-icon>Commissioni totali:  {{ Number(transferSummary.total_fees || 0).toFixed(2) }}</v-chip>
            </v-card-text>
          </v-card>

          <v-card class="neu-glow">
            <v-data-table
              :headers="transferHeaders"
              :items="transfers"
              :items-per-page="20"
              :footer-props="{ itemsPerPageOptions: [10,20,50] }"
              :loading="loadingTransfers"
              sort-by="created_at"
              sort-desc
              class="elevation-0"
              :no-data-text="'Nessun bonifico trovato'"
            >
              <template v-slot:[`item.amount_gross`]="{ item }">
                 {{ Number(item.amount_gross).toFixed(2) }}
              </template>
              <template v-slot:[`item.fee_total`]="{ item }">
                <span class="orange--text">-  {{ Number(item.fee_total).toFixed(2) }}</span>
                <span class="caption grey--text ml-1">({{ item.fee_variable_pct }}% + {{ item.fee_fixed }})</span>
              </template>
              <template v-slot:[`item.amount_net`]="{ item }">
                <span class="font-weight-bold teal--text"> {{ Number(item.amount_net).toFixed(2) }}</span>
              </template>
              <template v-slot:[`item.transfer_method`]="{ item }">
                <v-chip small :color="item.transfer_method === 'stripe' ? 'indigo' : 'teal'" dark>
                  <v-icon small left>{{ item.transfer_method === 'stripe' ? 'mdi-lightning-bolt' : 'mdi-bank' }}</v-icon>
                  {{ item.transfer_method === 'stripe' ? 'Stripe' : 'SEPA' }}
                </v-chip>
              </template>
              <template v-slot:[`item.status`]="{ item }">
                <v-chip small dark :color="transferStatusColor(item.status)">{{ transferStatusLabel(item.status) }}</v-chip>
              </template>
              <template v-slot:[`item.created_at`]="{ item }">
                <span class="caption">{{ formatDateTime(item.created_at) }}</span>
              </template>
              <template v-slot:[`item.expand`]="{ item }">
                <v-btn icon small @click="toggleTransferDetail(item)">
                  <v-icon small>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card>
        </v-tab-item>

      </v-tabs-items>
    </template>

    <!-- ── Dialog: capture / cancel / refund ──────────────────────── -->
    <v-dialog v-model="dialog_action" max-width="500" persistent>
      <v-card v-if="selectedPayment">
        <v-card-title class="text-h6">
          <v-icon class="mr-2" :class="pendingAction === 'capture' ? 'warning--text' : 'error--text'">
            {{ pendingAction === 'capture' ? 'mdi-cash-check' : pendingAction === 'refund' ? 'mdi-cash-refund' : 'mdi-cancel' }}
          </v-icon>
          {{ pendingAction === 'capture' ? 'Cattura Pagamento' : pendingAction === 'refund' ? 'Rimborsa Pagamento' : 'Annulla Autorizzazione' }}
        </v-card-title>
        <v-card-text>
          <!-- Capture: simple summary — no fees (full amount is collected from customer) -->
          <template v-if="pendingAction === 'capture'">
            <v-simple-table dense class="mb-3">
              <tbody>
                <tr>
                  <td class="grey--text">Cliente</td>
                  <td class="text-right font-weight-bold">{{ selectedPayment.full_name || selectedPayment.last_name || selectedPayment.full_phone }}</td>
                </tr>
                <tr>
                  <td class="grey--text">Data prenotazione</td>
                  <td class="text-right">{{ formatDate(selectedPayment.booking_date) }}</td>
                </tr>
                <tr>
                  <td class="grey--text font-weight-bold">Importo da catturare</td>
                  <td class="text-right font-weight-bold warning--text text-h6">{{ Number(selectedPayment.amount).toFixed(2) }} €</td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>

          <!-- Refund / Cancel: customer info + amount -->
          <template v-else>
            <v-simple-table dense class="mb-3">
              <tbody>
                <tr>
                  <td class="grey--text">Cliente</td>
                  <td class="text-right font-weight-bold">{{ selectedPayment.full_name || selectedPayment.last_name || selectedPayment.full_phone }}</td>
                </tr>
                <tr>
                  <td class="grey--text">Telefono</td>
                  <td class="text-right">{{ selectedPayment.full_phone }}</td>
                </tr>
                <tr>
                  <td class="grey--text">Data prenotazione</td>
                  <td class="text-right">{{ formatDate(selectedPayment.booking_date) }}</td>
                </tr>
                <tr v-if="selectedPayment.covers">
                  <td class="grey--text">Coperti</td>
                  <td class="text-right">{{ selectedPayment.covers }}</td>
                </tr>
                <tr>
                  <td class="grey--text font-weight-bold">{{ pendingAction === 'cancel' ? 'Importo autorizzazione' : 'Importo da rimborsare' }}</td>
                  <td class="text-right font-weight-bold error--text text-h6">{{ Number(selectedPayment.amount).toFixed(2) }} €</td>
                </tr>
              </tbody>
            </v-simple-table>
            <p class="caption grey--text mb-0">Ref: {{ selectedPayment.payment_intent_id }}</p>
          </template>

          <v-alert :type="pendingAction === 'capture' ? 'warning' : 'info'" dense text class="mt-2">
            <span v-if="pendingAction === 'capture'">Procedere solo in caso di no-show confermato.</span>
            <span v-else-if="pendingAction === 'cancel'">L'autorizzazione sarà rilasciata, il deposito non verrà mai addebitato.</span>
            <span v-else>L'importo sarà restituito sulla carta. Operazione non reversibile.</span>
          </v-alert>
        </v-card-text>
        <v-card-actions class="pb-4">
          <v-btn v-if="pendingAction !== 'capture' && selectedPayment.full_phone" outlined color="teal" :href="'tel:' + selectedPayment.full_phone">
            <v-icon left>mdi-phone</v-icon>
            Chiama
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn outlined color="grey" @click="dialog_action=false">Indietro</v-btn>
          <v-btn :color="pendingAction === 'capture' ? 'warning' : 'error'" :loading="capturing!==null||canceling!==null||refunding!==null" @click="doManagePayment()">
            <v-icon left>{{ pendingAction === 'capture' ? 'mdi-cash-check' : pendingAction === 'refund' ? 'mdi-cash-refund' : 'mdi-cancel' }}</v-icon>
            {{ pendingAction === 'capture' ? 'Cattura' : pendingAction === 'refund' ? 'Rimborsa' : 'Annulla' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Dialog: transfer to restaurant ─────────────────────────── -->
    <v-dialog v-model="dialog_transfer" max-width="560" persistent>
      <v-card>
        <v-card-title class="text-h6 info white--text">
          <v-icon left dark>mdi-bank-transfer</v-icon>
          Trasferisci al ristorante
        </v-card-title>
        <v-card-text class="pt-4">
          <v-simple-table dense class="mb-3">
            <tbody>
              <tr><td class="grey--text">Pagamenti selezionati</td><td class="text-right font-weight-bold">{{ selectedPayments.length }}</td></tr>
              <tr><td class="grey--text">Importo lordo</td><td class="text-right"> {{ transferGross }}</td></tr>
              <tr><td class="grey--text">Fee iBook</td><td class="text-right orange--text">- {{ transferApplicationFee }}</td></tr>
              <tr><td class="grey--text">Fee Stripe</td><td class="text-right orange--text">- {{ transferStripeFee }}</td></tr>
              <tr class="font-weight-bold"><td>Netto da trasferire</td><td class="text-right teal--text text-h6"> {{ transferNet }}</td></tr>
            </tbody>
          </v-simple-table>

          <v-alert v-if="transferMethod === 'direct_charge'" type="info" dense text>
            I fondi sono già sul vostro account Stripe Connect. La commissione di piattaforma è già stata trattenuta al momento del pagamento.
          </v-alert>
          <v-alert v-else type="error" dense text>
            Nessun account Stripe Connect configurato. Collegare l'account nelle impostazioni Stripe.
          </v-alert>
        </v-card-text>
        <v-card-actions class="justify-end pb-4">
          <v-btn outlined color="grey" @click="dialog_transfer=false">Indietro</v-btn>
          <v-btn color="info" :loading="transferring" :disabled="!transferMethod" @click="doTransfer()">
            <v-icon left>mdi-bank-transfer</v-icon>
            Registra liquidazione
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Dialog: date range picker ──────────────────────────────── -->
    <v-dialog v-model="dialog_range" persistent width="600px">
      <v-card class="neu-glow pb-1">
        <v-card-title>
          Scegli Periodo
          <v-spacer></v-spacer>
          <v-btn icon fab small class="neu-glow" @click="dialog_range=false; reloadAll()">
            <v-icon color="info">mdi-check</v-icon>
          </v-btn>
        </v-card-title>
        <v-card class="neu-glow ma-4">
          <v-date-picker v-model="dateRange" scrollable range color="info" show-adjacent-months full-width :first-day-of-week="1" locale="it" show-week></v-date-picker>
        </v-card>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
  name: 'Payments',
  data() {
    return {
      activeTab: 0,
      loading: false,
      payments: [],
      selectedPayments: [],
      search: '',
      filterStatus: null,
      capturing: null,
      canceling: null,
      refunding: null,
      transferring: false,
      dialog_action: false,
      dialog_transfer: false,
      dialog_range: false,
      pendingAction: null,
      selectedPayment: null,
      // Transfers tab
      transfers: [],
      transferSummary: null,
      loadingTransfers: false,
      transfersLoaded: false,
      // Snackbar
      snackbar: false,
      snackbar_color: null,
      snackbar_message: null,
      // Date range (default last 30 days)
      dateRange: [
        new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().substring(0, 10),
        new Date().toISOString().substring(0, 10)
      ],
      statusOptions: [
        { text: 'Catturato',      value: 'succeeded'        },
        { text: 'Da Catturare',   value: 'requires_capture' },
        { text: 'Cancellato',     value: 'canceled'         },
        { text: 'Fallito',        value: 'failed'           },
        { text: 'Trasferito',     value: 'transferred'      },
      ],
      headers: [
        { text: 'Cliente',     value: 'full_name',    width: '140px', tooltip: 'Nome e cognome del cliente' },
        { text: 'Telefono',    value: 'full_phone',   width: '120px', tooltip: 'Numero di telefono del cliente' },
        { text: 'Coperti',     value: 'covers',       width: '80px',  tooltip: 'Numero di coperti della prenotazione' },
        { text: 'Data Pr.',    value: 'booking_date', width: '110px', tooltip: 'Data della prenotazione' },
        { text: 'Data Pag.',   value: 'created_at',   width: '140px', tooltip: 'Data e ora in cui il pagamento è stato creato su Stripe' },
        { text: 'Importo',     value: 'amount',             width: '90px',  tooltip: 'Importo del deposito/pagamento in EUR' },
        { text: 'Fee iBook',   value: 'application_fee_amount', width: '90px',  tooltip: 'Commissione trattenuta dalla piattaforma iBook (application fee)' },
        { text: 'Fee Stripe',  value: 'stripe_fees',            width: '90px',  tooltip: 'Commissioni trattenute da Stripe sul pagamento' },
        { text: 'Netto',       value: 'net_amount',             width: '90px',  tooltip: 'Importo netto al ristorante (Importo − Fee iBook − Fee Stripe)' },
        { text: 'Stato',       value: 'status',                 width: '130px', tooltip: 'Stato attuale del pagamento Stripe' },
        { text: 'Eligible',    value: 'eligible',     width: '90px',  tooltip: 'L\'evento è passato, il cliente non risulta arrivato e la somma era solo bloccata', sortable: true },
        { text: '> 7gg',       value: 'exceed',       width: '80px',  tooltip: 'Il blocco fondi non può superare i 7 giorni. Se attendi il "no-show", rischi che i fondi non siano più disponibili.', sortable: true },
        { text: 'No Show',     value: 'not_arrived',  width: '80px',  tooltip: 'Non arrivato al Locale', sortable: false },
        { text: 'Ore rimaste', value: 'time_left',    width: '110px', tooltip: 'Ore rimanenti prima che l\'autorizzazione scada automaticamente su Stripe' },
        { text: 'Azioni',      value: 'actions',      width: '130px', sortable: false, tooltip: '' },
      ],
      transferHeaders: [
        { text: 'Data',         value: 'created_at',      width: '140px' },
        { text: 'Lordo',        value: 'amount_gross',    width: '90px'  },
        { text: 'Commissioni',  value: 'fee_total',       width: '180px' },
        { text: 'Netto',        value: 'amount_net',      width: '100px' },
        { text: 'Metodo',       value: 'transfer_method', width: '100px' },
        { text: 'Stato',        value: 'status',          width: '110px' },
        { text: 'N° pagam.',    value: 'payment_count',   width: '90px'  },
        { text: 'Stripe ID',    value: 'stripe_transfer_id', width: '160px' },
        { text: '',             value: 'expand',          width: '40px', sortable: false },
      ],
    }
  },

  created() {
    if (this.settings && this.settings.userid) this.getData()
  },

  computed: {
    ...mapGetters(['settings']),

    rangeString() {
      const opts = { year: 'numeric', month: '2-digit', day: '2-digit' }
      const a = new Date(this.dateRange[0]).toLocaleDateString('it', opts)
      const b = this.dateRange[1] ? new Date(this.dateRange[1]).toLocaleDateString('it', opts) : a
      return a + ' - ' + b
    },

    filteredPayments() {
      return (this.payments || []).filter(p => {
        if (this.filterStatus === 'transferred') {
          // Show only transferred payments when that filter is active
          if (!p.transfer_id) return false
        } else {
          // By default hide transferred payments; they live in Storni / Bonifici
          if (p.transfer_id) return false
          if (this.filterStatus && p.status !== this.filterStatus) return false
        }
        if (this.search) {
          const q = this.search.toLowerCase()
          if (!(p.last_name || '').toLowerCase().includes(q) && !(p.full_phone || '').toLowerCase().includes(q)) return false
        }
        return true
      })
    },

    totalSucceeded()      { return this.filteredPayments.filter(p => p.status === 'succeeded').reduce((s, p) => s + Number(p.amount || 0), 0).toFixed(2) },
    totalRequiresCapture(){ return this.filteredPayments.filter(p => p.status === 'requires_capture').reduce((s, p) => s + Number(p.amount || 0), 0).toFixed(2) },
    totalCanceled()       { return this.filteredPayments.filter(p => p.status === 'canceled').reduce((s, p) => s + Number(p.amount || 0), 0).toFixed(2) },

    // Transfer fee calculations
    actionFeeFixed() {
      return Number((this.settings && this.settings.options && this.settings.options.stripeConnectVariableFees) || 0).toFixed(2)
    },
    actionFeeVariable() {
      const pct = Number((this.settings && this.settings.options && this.settings.options.stripeConnectFixedFees) || 0)
      const gross = this.selectedPayment ? Number(this.selectedPayment.amount || 0) : 0
      return (gross * pct / 100).toFixed(2)
    },
    actionNet() {
      const gross = this.selectedPayment ? Number(this.selectedPayment.amount || 0) : 0
      return Math.max(0, gross - Number(this.actionFeeFixed) - Number(this.actionFeeVariable)).toFixed(2)
    },

    transferGross() {
      return this.selectedPayments
        .filter(p => p.status === 'succeeded' && !p.transfer_id)
        .reduce((s, p) => s + Number(p.amount || 0), 0).toFixed(2)
    },
    transferApplicationFee() {
      return this.selectedPayments
        .filter(p => p.status === 'succeeded' && !p.transfer_id)
        .reduce((s, p) => s + Number(p.application_fee_amount || 0), 0).toFixed(2)
    },
    transferStripeFee() {
      return this.selectedPayments
        .filter(p => p.status === 'succeeded' && !p.transfer_id)
        .reduce((s, p) => s + Number(p.stripe_fees || 0), 0).toFixed(2)
    },
    transferNet() {
      return Math.max(0, Number(this.transferGross) - Number(this.transferApplicationFee) - Number(this.transferStripeFee)).toFixed(2)
    },

    // Rows that can be selected (succeeded + not yet transferred)
    selectablePayments() {
      return this.filteredPayments.filter(p => p.status === 'succeeded' && !p.transfer_id)
    },
    // Selected rows that are actually transferable (guard against stale selection)
    transferablePayments() {
      return this.selectedPayments.filter(p => p.status === 'succeeded' && !p.transfer_id)
    },

    // Detect which method to use based on settings
    transferMethod() {
      const opts = this.settings && this.settings.options
      if (!opts) return null
      if (opts.stripeConnectAccountId) return 'direct_charge'
      return null
    },
  },

  watch: {
    'settings.userid': {
      immediate: false,
      handler(val) { if (val && !this.payments.length) this.getData() }
    }
  },

  methods: {
    // ── isSelectable: used in per-row slot
    isSelectable(item) {
      return item.status === 'succeeded' && !item.transfer_id
    },

    // ── Toggle select-all for eligible rows only
    toggleSelectAll(val) {
      if (val) {
        const toAdd = this.selectablePayments.filter(p => !this.selectedPayments.some(s => s.payment_intent_id === p.payment_intent_id))
        this.selectedPayments = [...this.selectedPayments, ...toAdd]
      } else {
        this.selectedPayments = []
      }
    },

    reloadAll() {
      this.getData()
      if (this.activeTab === 1) this.loadTransfers(true)
    },

    // ── TAB 1: Load payments ────────────────────────────────────────
    async getData() {
      this.loading = true
      try {
        const res = await this.$http.post('/stripe/list-payments.php', {
          csrf: sessionStorage.getItem('csrf'),
          payload: {
            action:   'list-payments',
            userId:   this.settings && this.settings.userid || null,
            status:   this.filterStatus || null,
            dateFrom: this.dateRange[0] || null,
            dateTo:   this.dateRange[1] || null,
          }
        })
        const newCsrf = res.headers['x-csrf-token']; if (newCsrf) sessionStorage.setItem('csrf', newCsrf)
        const newJwt  = res.headers['authorization']; if (newJwt) { this.$http.defaults.headers.common['Authorization'] = newJwt; sessionStorage.setItem('jwt', newJwt) }
        if (res.data.errorid === '0') {
          this.payments = res.data.payloads && res.data.payloads.payments || []
          this.selectedPayments = []
        } else {
          this.showSnackbar(res.data.message || 'Errore nel caricamento', 'error')
          setTimeout(() => { this.$router.push('/auth/login').catch(() => {}) }, 5000)
        }
      } catch (e) { console.error('getData error:', e); this.showSnackbar('Errore di connessione', 'error'); setTimeout(() => { this.$router.push('/auth/login').catch(() => {}) }, 3000) }
      this.loading = false
    },

    // ── TAB 2: Load transfers history ───────────────────────────────
    async loadTransfers(force = false) {
      if (this.transfersLoaded && !force) return
      this.loadingTransfers = true
      try {
        const res = await this.$http.post('/stripe/list-transfers.php', {
          csrf: sessionStorage.getItem('csrf'),
          payload: { dateFrom: this.dateRange[0], dateTo: this.dateRange[1] }
        })
        const newCsrf = res.headers['x-csrf-token']; if (newCsrf) sessionStorage.setItem('csrf', newCsrf)
        const newJwt  = res.headers['authorization']; if (newJwt) { this.$http.defaults.headers.common['Authorization'] = newJwt; sessionStorage.setItem('jwt', newJwt) }
        if (res.data.errorid === '0') {
          this.transfers       = res.data.payloads && res.data.payloads.transfers || []
          this.transferSummary = res.data.payloads && res.data.payloads.summary   || null
          this.transfersLoaded = true
        } else {
          this.showSnackbar(res.data.message || 'Errore caricamento bonifici', 'error')
          setTimeout(() => { this.$router.push('/auth/login').catch(() => {}) }, 5000)
        }
      } catch (e) { console.error('loadTransfers error:', e); this.showSnackbar('Errore di connessione', 'error') }
      this.loadingTransfers = false
    },

    // ── Capture / Cancel / Refund ────────────────────────────────────
    confirmAction(action, payment) {
      this.pendingAction  = action
      this.selectedPayment = payment
      this.dialog_action  = true
    },

    async doManagePayment() {
      const action = this.pendingAction
      const lf  = { capture: 'capturing', cancel: 'canceling', refund: 'refunding' }[action]
      const ok  = { capture: 'Pagamento catturato con successo', cancel: 'Autorizzazione annullata', refund: 'Rimborso effettuato con successo' }[action]
      const err = { capture: 'Errore durante la cattura', cancel: 'Errore annullamento', refund: 'Errore rimborso' }[action]
      this[lf] = this.selectedPayment.payment_intent_id
      try {
        const res = await this.$http.post('/stripe/manage-payment.php', {
          csrf: sessionStorage.getItem('csrf'),
          payload: { action, paymentIntentId: this.selectedPayment.payment_intent_id, amount: this.selectedPayment.amount }
        })
        const newCsrf = res.headers['x-csrf-token']; if (newCsrf) sessionStorage.setItem('csrf', newCsrf)
        const newJwt  = res.headers['authorization']; if (newJwt) { this.$http.defaults.headers.common['Authorization'] = newJwt; sessionStorage.setItem('jwt', newJwt) }
        if (res.data.errorid === '0') {
          this.showSnackbar(ok, 'success')
          this.dialog_action = false; this.selectedPayment = null; this.pendingAction = null
          await this.getData()
        } else {
          this.showSnackbar(res.data.message || err, 'error')
          setTimeout(() => { this.$router.push('/auth/login').catch(() => {}) }, 5000)
        }
      } catch (e) { console.error('manage-payment error:', e); this.showSnackbar('Errore di connessione', 'error') }
      this[lf] = null
    },

    // ── Execute transfer ─────────────────────────────────────────────
    async doTransfer() {
      this.transferring = true
      try {
        const res = await this.$http.post('/stripe/transfer-payments.php', {
          csrf: sessionStorage.getItem('csrf'),
          payload: {
            payments: this.selectedPayments.map(p => ({
              paymentId:       p.payment_id,
              paymentIntentId: p.payment_intent_id,
              amount:          p.amount,
            }))
          }
        })
        const newCsrf = res.headers['x-csrf-token']; if (newCsrf) sessionStorage.setItem('csrf', newCsrf)
        const newJwt  = res.headers['authorization']; if (newJwt) { this.$http.defaults.headers.common['Authorization'] = newJwt; sessionStorage.setItem('jwt', newJwt) }
        if (res.data.errorid === '0') {
          this.showSnackbar(res.data.message, 'success')
          this.dialog_transfer = false
          this.selectedPayments = []
          this.transfersLoaded = false  // force reload of tab 2
          await this.getData()
        } else {
          this.showSnackbar(res.data.message || 'Errore trasferimento', 'error')
          setTimeout(() => { this.$router.push('/auth/login').catch(() => {}) }, 5000)
        }
      } catch (e) { console.error('doTransfer error:', e); this.showSnackbar('Errore di connessione', 'error') }
      this.transferring = false
    },

    toggleTransferDetail(item) {
      // Future: expand row to show individual payments in the transfer
      console.log('Transfer detail:', item)
    },

    // ── Helpers ─────────────────────────────────────────────────────
    statusColor(s)  { return { succeeded:'success', requires_capture:'warning', canceled:'grey', failed:'error' }[s] || 'grey' },
    statusIcon(s)   { return { succeeded:'mdi-check-circle', requires_capture:'mdi-clock-alert', canceled:'mdi-cancel', failed:'mdi-close-circle' }[s] || 'mdi-help-circle' },
    statusLabel(s)  { return { succeeded:'Catturato', requires_capture:'Catturabile', canceled:'Cancellato', failed:'Fallito' }[s] || s },
    transferStatusColor(s) { return { completed:'teal', pending:'orange', failed:'error', cancelled:'grey' }[s] || 'grey' },
    transferStatusLabel(s) { return { completed:'Completato', pending:'In attesa', failed:'Fallito', cancelled:'Annullato' }[s] || s },

    formatDate(d) {
      if (!d) return '-'
      return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
    },
    formatDateTime(dt) {
      if (!dt) return '-'
      const d = new Date(dt)
      return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
    },
    showSnackbar(message, color = 'info') {
      this.snackbar_message = message
      this.snackbar_color   = color
      this.snackbar         = true
    },
    isExceed(item) {
      // If backend already marks exceed, respect it
      if (item && item.exceed) return true
      // Only relevant for uncaptured authorizations
      if (!item || item.status !== 'requires_capture') return false
      // Determine threshold from settings (stripeChargeDays) default 7
      const opts = this.settings && this.settings.options
      const chargeDays = Number((opts && opts.stripeChargeDays) || 7)
      // Parse dates
      const booking = item.booking_date ? new Date(item.booking_date) : null
      const created = item.created_at ? new Date(item.created_at) : null
      if (!booking || !created) return false
      // Difference in days between booking date and payment creation
      const diffMs = booking.getTime() - created.getTime()
      const diffDays = diffMs / (1000 * 60 * 60 * 24)
      return diffDays > chargeDays
    },
  }
}
</script>
